import { useMemo, useState } from 'react';
import { DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { useGetInvitations, useRemoveInvitation } from '@/shared/api';
import { useRouterPrivateContext } from '@/shared/router';
import { Table } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';
import { InviteMembersModal } from './InviteMembersModal';

export const InvitationsTab = () => {
  const { selectedWorkspace } = useRouterPrivateContext();

  const [modalOpen, setModalOpen] = useState(false);

  const {
    data: invitationsData,
    refetch: refetchInvitations,
    isLoading: invitationDataLoading,
  } = useGetInvitations(
    {
      pathParams: { workspaceId: selectedWorkspace?.id! },
      queryParams: {
        order: 'DESC',
      },
    },
    { enabled: Boolean(selectedWorkspace?.id), select: (data) => data.data },
  );
  const { mutate: removeInvitation } = useRemoveInvitation();

  const columns = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'index',
        key: 'index',
        render: (_, record, index) => index + 1,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
      },
      {
        title: 'Action',
        key: 'action',
        render: (_, record) => (
          <Button
            type="text"
            icon={
              <Tooltip title="Remove invitation">
                <DeleteOutlined />
              </Tooltip>
            }
            onClick={() => handleInvitationRemove(record.id)}
          />
        ),
      },
    ],
    [],
  );

  const handleInvitationRemove = (id: string) => {
    try {
      removeInvitation(
        {
          pathParams: {
            workspaceId: selectedWorkspace?.id!,
            invitationId: id,
          },
        },
        { onSuccess: refetchInvitations },
      );
    } catch (error) {
      captureError(error, {
        module: 'workspace_tabs_on_error_remove_invitation',
        action: 'remove_invitation',
      });
    }
  };

  return (
    <div data-testid="workspace-invitations-tab">
      <Button
        type="primary"
        icon={<UserAddOutlined />}
        onClick={() => setModalOpen(true)}
        style={{ marginBottom: 20 }}
      >
        Invite members
      </Button>
      <Table
        data-testid="workspace-invitations-table"
        dataSource={invitationsData}
        loading={invitationDataLoading}
        columns={columns}
        rowKey={(invitation) => invitation.id}
      />
      <InviteMembersModal
        open={modalOpen}
        refetchInvitations={refetchInvitations}
        onCancel={() => setModalOpen(false)}
      />
    </div>
  );
};
